import type { IslandProps } from '../registry'
import { EmptyState, Skeleton } from '../../shared/ui'
import { groupByBusiness } from '../../lib/sources'
import { EmailSourceCard } from './EmailSourceCard'
import { useBusinessSources } from './useBusinessSources'
import { SOURCES_CSS } from './sourcesStyles'

/**
 * Settings > Email: where each business connects its own mailbox
 * (docs/proposals/business-sources.md).
 *
 * Lists every business the user belongs to, the current one first, each
 * with all of its inboxes (Gmail / Google Workspace and Zoho Mail, as many as
 * it sends from). Replaces the legacy single "Gmail sync" card, which
 * connected one Gmail per login.
 */
export default function SourcesIsland(_props: IslandProps) {
  const { sources, status, error, refetch } = useBusinessSources()

  if (status === 'loading') {
    return (
      <div aria-busy="true" aria-label="Loading">
        <Skeleton width="40" />
        <Skeleton />
      </div>
    )
  }
  if (status === 'error') {
    return (
      <EmptyState
        role="alert"
        title="Could not load your email sources"
        message={error ?? 'Something went wrong.'}
        cta={{ label: 'Retry', onClick: refetch }}
      />
    )
  }

  const ordered = groupByBusiness(sources).sort(
    (a, b) => Number(b.is_current) - Number(a.is_current),
  )
  return (
    <div className="src-page">
      <style>{SOURCES_CSS}</style>
      <ul className="src-list" aria-label="Email per business">
        {ordered.map((g) => (
          <EmailSourceCard key={g.workspace_id} business={g} onChanged={refetch} />
        ))}
      </ul>
      <p className="src-foot">
        Connect every inbox a business sends from. Hunts reads only mail to and from your contacts
        in that business, from the last 90 days onward, and puts it all on the same contacts and
        deals. Calendars stay per person, under Calendar.
      </p>
    </div>
  )
}
